import React from "react";
import { Calendar, Wallet, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

type AccountLite = {
  id: string;
  name: string | null;
  account_type?: string | null;
  currency?: string | null;
};

export type ReconciliationFiltersState = {
  selectedAccountId: string | null;
  dateFrom: string; // YYYY-MM-DD
  dateTo: string;   // YYYY-MM-DD
};

const ALL = "__all__";

function pad2(n: number) {
  return String(n).padStart(2, "0");
}
function toYMD(d: Date) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function addDays(d: Date, days: number) {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
}
function startOfPrevMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth() - 1, 1);
}
function endOfPrevMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 0);
}

export function ReconciliationFilters(props: {
  value: ReconciliationFiltersState;
  onChange: (next: ReconciliationFiltersState) => void;
  onResetSelection?: () => void;

  accounts: AccountLite[];
  isAccountsLoading?: boolean;
  accountsError?: string | null;

  className?: string;
}) {
  const {
    value,
    onChange,
    onResetSelection,
    accounts,
    isAccountsLoading,
    accountsError,
    className,
  } = props;

  const sortedAccounts = React.useMemo(() => {
    return [...accounts].sort((a, b) =>
      (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" })
    );
  }, [accounts]);

  const setRange = (from: Date, to: Date) => {
    onChange({ ...value, dateFrom: toYMD(from), dateTo: toYMD(to) });
  };

  const setYesterday = () => {
    const t = new Date();
    const y = addDays(t, -1);
    setRange(y, y);
  };

  const setToday = () => {
    const t = new Date();
    setRange(t, t);
  };

  const setLast7Days = () => {
    const t = new Date();
    setRange(addDays(t, -6), t); // includes today
  };

  const setCurrentMonth = () => {
    const t = new Date();
    setRange(startOfMonth(t), t); // month-to-date
  };

  const setLastMonth = () => {
    const t = new Date();
    setRange(startOfPrevMonth(t), endOfPrevMonth(t));
  };

  const handleReset = () => {
    const t = new Date();
    onChange({
      selectedAccountId: null,
      dateFrom: toYMD(startOfMonth(t)),
      dateTo: toYMD(t),
    });
    onResetSelection?.();
  };

  const labelCls =
    "h-4 leading-4 text-[10px] font-black text-slate-500 uppercase tracking-widest";

  const quickBtnCls = cn(
    "h-7 px-3 rounded-lg border border-slate-200 bg-white",
    "text-xs font-semibold text-slate-700 hover:bg-slate-50",
    "whitespace-nowrap"
  );

  return (
    <div className={cn("w-full", className)}>
      <div
        className={cn(
          "grid grid-cols-1 gap-4",
          "lg:grid-cols-[360px_1fr_auto] lg:items-end lg:gap-6"
        )}
      >
        {/* Account */}
        <div className="grid gap-2">
          <div className={labelCls}>Account</div>

          {isAccountsLoading ? (
            <Skeleton className="h-10 w-full rounded-lg" />
          ) : (
            <Select
              value={value.selectedAccountId ?? ALL}
              onValueChange={(v) =>
                onChange({ ...value, selectedAccountId: v === ALL ? null : v })
              }
            >
              <SelectTrigger className="h-10 w-[300px] rounded-lg bg-slate-50 border-slate-200">
                <Wallet className="h-4 w-4 mr-2 text-slate-400" />
                <SelectValue placeholder="All accounts" />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value={ALL}>All accounts</SelectItem>

                {sortedAccounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {(a.name ?? "Unnamed") +
                      (a.account_type ? ` — ${a.account_type}` : "") +
                      (a.currency ? ` (${a.currency})` : "")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {accountsError ? (
            <div className="text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-2 py-1">
              {accountsError}
            </div>
          ) : null}
        </div>

        {/* Dates + only gaps */}
        <div className="grid gap-2">
          <div className="flex items-center justify-between gap-3">
            <div className={labelCls}>Period</div>
          </div>

          <div className="h-10 flex items-center gap-2 px-2 bg-slate-50 border border-slate-200 rounded-lg shadow-sm">
            <Calendar className="h-4 w-4 text-slate-400" />

            <Input
              type="date"
              value={value.dateFrom}
              onChange={(e) => {
                const nextFrom = e.target.value;
                // If new From is after current To, auto-bump To to From + 7 days
                if (nextFrom && value.dateTo && nextFrom > value.dateTo) {
                  const [y, m, d] = nextFrom.split("-").map(Number);
                  const fromDate = new Date(y, (m ?? 1) - 1, d ?? 1);
                  const bumpedTo = addDays(fromDate, 7);
                  onChange({ ...value, dateFrom: nextFrom, dateTo: toYMD(bumpedTo) });
                } else {
                  onChange({ ...value, dateFrom: nextFrom });
                }
              }}
              className={cn(
                "h-8 w-[140px] border-none bg-transparent hover:bg-white",
                "focus-visible:ring-0 text-xs font-semibold"
              )}
              title="Start Date"
            />

            <span className="text-slate-300">—</span>

            <Input
              type="date"
              value={value.dateTo}
              min={value.dateFrom || undefined}
              onChange={(e) => {
                const nextTo = e.target.value;
                onChange({ ...value, dateTo: nextTo });
              }}
              className={cn(
                "h-8 w-[140px] border-none bg-transparent hover:bg-white",
                "focus-visible:ring-0 text-xs font-semibold"
              )}
              title="End Date"
            />
          </div>
        </div>

        {/* Quick ranges */}
        <div className="lg:ml-3 flex lg:items-center">
          <div className={cn("mt-2 lg:mt-0", "self-center my-auto")}>
            <div className="flex flex-wrap gap-2 justify-center lg:justify-center">
              <button type="button" onClick={setYesterday} className={quickBtnCls}>
                Yesterday
              </button>
              <button type="button" onClick={setToday} className={quickBtnCls}>
                Today
              </button>
              <button type="button" onClick={setLast7Days} className={quickBtnCls}>
                Last 7 days
              </button>
              <button type="button" onClick={setCurrentMonth} className={quickBtnCls}>
                Current month
              </button>
              <button type="button" onClick={setLastMonth} className={quickBtnCls}>
                Last month
              </button>

              <button
                type="button"
                onClick={handleReset}
                className={cn(quickBtnCls, "inline-flex items-center gap-2")}
                title="Reset filters"
              >
                <RotateCcw className="h-4 w-4" />
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-0 lg:hidden" />
    </div>
  );
}